// TODO: Upload logo states: in-progress, success, failure
import React, { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import _ from 'lodash';
import axios, { AxiosError } from 'axios';
import ColorThief from 'colorthief';
import PapaParse from 'papaparse';

import { useMicrosoft } from '../../_libs/hooks/useMicrosoft';
import uploadLogoFileWithOptions from '../../_libs/utils/uploadLogoFileWithOptions';
import StandardPageTemplate from '../../templates/StandardPage';
import ImageFromAzureWithUri from '../../atoms/ImageFromAzureWithUri';
import Message from '../../atoms/Message';
import Table from '../../atoms/Table';
import TableHead from '../../atoms/TableHead';
import TableHeader from '../../atoms/TableHeader';
import TableBody from '../../atoms/TableBody';
import TableRow from '../../atoms/TableRow';
import TableData from '../../atoms/TableData';

function formatBucketFolder (campaignId: string | number) {
  return `campaigns/${campaignId}/logos`;
}

async function uploadLogo (
  file: File, 
  campaignId: string,
  graphUserId: string,
  handleError: (fileName: string) => void, 
  handleSuccess: (fileName: string) => void,
) {
  const logo_url = await uploadLogoFileWithOptions(
    file,
    {
      container: graphUserId,
      folderPath: formatBucketFolder(campaignId),
    },
  );
  if (logo_url) {
    handleSuccess(file.name);
  } else {
    handleError(file.name);
  }
}

const UploadLogos = ({ campaignId }): JSX.Element => {
  const {
    graphData
  } = useMicrosoft();
  const { 
    formState: { errors },
    handleSubmit,
    register,
    watch
  } = useForm();
  const [files, setFiles] = useState<FileList>(null);
  const [uploadErrors, updateUploadErrors] = useState<string[]>([]);
  const [uploadSuccesses, updateUploadSuccesses] = useState<string[]>([]);
  const [uploadResult, setUploadResult] = useState<JSX.Element>(null);
  const [isUploading, setIsUploading] = useState(false);

  // On change, handleFileChange
  function handleFileChange (files: FileList) {
    if (files.length > 0) {
      setFiles(files);
      setUploadResult(null);
      setIsUploading(false);
      updateUploadSuccesses([]);
      updateUploadErrors([]);
    }
  }
  function handleUploadError (fileName: string) {
    updateUploadErrors((prevState: string[]) => { return prevState.concat(fileName); });
    console.error(`Could not upload ${fileName}.`);
  }
  function handleUploadSuccess (fileName: string) {
    updateUploadSuccesses((prevState: string[]) => { return prevState.concat(fileName); });
  }
  // On submit, handleUploadLogos
  function onSubmit (data: FieldValues) {
    if (data.files.length > 0) {
      setIsUploading(true);
      setUploadResult(null);
      _.each(data.files, (file: File) => {
        // FIXME: Potential closure issue
        uploadLogo(file, campaignId, graphData?.id, handleUploadError, handleUploadSuccess);
      });
    }
  }
  useEffect(() => {
    if (files) {
      if ((uploadErrors.length + uploadSuccesses.length) === files.length) {
        if (uploadErrors.length) {
          setFiles(null);
          setIsUploading(false);
          setUploadResult(
            <Message type="error">
              <strong>Upload finished with errors. </strong> 
              { `Could not upload ${uploadErrors.length} ${uploadErrors.length === 1 ? 'file' : 'files'}: ${uploadErrors.join(', ')}.` }
            </Message>
          );
        } else {
          setFiles(null);
          setIsUploading(false);
          setUploadResult(
            <Message type="success">
              <strong>Success.</strong> Logos uploaded successfully. Refresh the page to see results.
            </Message>
          );
        }
      } else if (
          (uploadErrors.length !== 0 && uploadSuccesses.length !== 0) &&
          (uploadErrors.length + uploadSuccesses.length) !== files.length
        ) {
        setFiles(null);
        setIsUploading(false);
        setUploadResult(
          <Message type="error">
            <strong>Upload finished with errors. </strong> 
            { 'The number of logos processed appears to differ from the files uploaded. Please refresh the page and review which logos may need to be uploaded again.' }
            { uploadErrors.length > 0 &&
                `Could not upload ${uploadErrors.length} ${uploadErrors.length === 1 ? 'file' : 'files'}.`
            }
          </Message>
        );
      }
    }
  }, [files, uploadErrors, uploadSuccesses]);

  watch ( (data, { name, type }) => {
    if (name === 'files' && type === 'change' && data.files.length >= 1) {
      handleFileChange(data.files as FileList);
    }
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h1 className="f-serif f-normal">Upload Logos</h1>
      <p>Files must be .jpeg, .jpg, or .png. Logos with a white or transparent background work best.</p>
      <div>
        <label className="button mb-0">
          Choose logo(s) to upload.
          <input
            accept=".jpeg,.jpg,.png"
            type="file"
            multiple
            {
              ...register(
                'files',
                { 
                  required: 'A .jpeg, .jpg, or .png file is required.',
                }
              )
            } />
        </label>
        {files && <span className="f-small f-italic ml-2">{`${files.length} ${files.length === 1 ? 'file' : 'files'} selected`}</span>}
      </div>
      {
        errors.files &&
          <p>{errors.files.message.toString()}</p>
      }
      { isUploading 
        ?
          <button type="button" className="mb-4" disabled>Uploading Logos...</button>
        :
          <button type="submit" className="mb-4" disabled={!files }>Upload Logos</button>
      } 
      { uploadResult }
    </form>
  );
};

function fetch_campaign (campaignId, cb) {
  axios.get(`/fetch_campaign/${campaignId}/`)
    .then((results) => {
      if (results.data.status !== 'OK') {
        throw new Error(results.data.message);
      }
      cb(results.data.campaign);
    });
}

function fetch_leads (campaignId, cb) {
  axios.get(`/fetch_leads_for_campaign/${campaignId}/`)
    .then((results) => {
      if (results.data.status !== 'OK') {
        throw new Error(results.data.message);
      }
      cb(results.data.leads);
    });
}

// via: https://stackoverflow.com/questions/52240221/download-save-csv-file-papaparse
function handleDownload (data, name) {
  const csv = PapaParse.unparse(data);
  const csvBlob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const csvUrl = window.URL.createObjectURL(csvBlob);
  const tempLink = document.createElement('a');
  tempLink.href = csvUrl;
  // Currently, name includes a '.csv' suffix
  tempLink.setAttribute('download', `campaign_${name}`);
  tempLink.click();
}

// TODO: Generate PDF and post in AzureStorage
function handleGenerate (data, campaignId) {
  axios
    .get(`/generate_report_cards/${campaignId}/0/10`)
    .then(
      () => {
        // console.log(res);
      },
      (err: AxiosError) => {
        console.error(err);
      },
    );
  _.each(data, () => {
    // Check that data is valid
    if (data.brandColor) {
      // Generate PDF
      // Upload blob to Amazon
      // Store returned URL in database
      // Generate summary
      // Upload blob to Amazon
      // Store returned URL in database
    } else {
      return;
    }
  });
}

const BrandColorSelector = ({
  data,
  leadId,
  organizationId,
  reportId,
}): JSX.Element => {
  const [ showSelector, toggleShowSelector ] = useState(false);
  const [ currColor, setCurrColor ] = useState(data);

  let content: any;
  if (currColor) {
    content =
      <div className="mb-4" style={{
        display: 'flex',
        flexDirection: 'row',
      }}>
        <div
          style={{
            background: currColor,
            width: '25px',
            height: '25px',
            borderRadius: '3px',
            marginRight: '0.25rem',
          }}
        ></div>
        <div style={{
          lineHeight: '25px',
          display: 'inline-block',
        }}>{currColor}</div>
      </div>;
  } else {
    content =
      <div
        style={{
          marginBottom: '1rem',
        }}
      >
        No Color Chosen
      </div>;
  }
  if (showSelector) {
    const logo: HTMLImageElement = document.getElementById(`js-logo-${leadId}`) as HTMLImageElement;
    const colors = getColorFromLogo(logo);
    const colorEls = [];
    _.each(colors, (color, i) => {
      colorEls.push(
        <button type="button"
          key={i} 
          style={{
            background: color,
            width: '25px',
            height: '25px',
            borderRadius: '3px',
            display: 'inline-block',
            marginRight: '0.25rem',
            marginBottom: '1rem',
          }}
          onClick={
            () => {
              setBrandColorForOrganization(color, organizationId, reportId);
              setCurrColor(color);
              toggleShowSelector(false);
            }
          }
        >
        </button>
      );
      content = colorEls;
    });
    setLogoDimensionsForOrganization(logo, organizationId, {});
  }
  return (
    <>
      <button type="button" className="mt-4 mb-2" style={{ borderColor: 'black' }} onClick={() => { toggleShowSelector(!showSelector); }}>{ showSelector ? 'Cancel' : 'Choose Color' }</button>
      <div>
        {content}
      </div>
    </>
  );
};

const LeadListItemCol = ({ campaignId, leadId, organizationId, reportId, data, isBrandColor, isLogo, isLink }): JSX.Element => {
  const [ imageSize, toggleImageSize ] = useState('2rem');
  let content = data;
  if (isBrandColor) {
    const params = {
      data,
      leadId,
      organizationId,
      reportId,
    };
    content = <BrandColorSelector {...params} />;
  }
  if (isLogo) {
    if (data && data.length > 0) {   
      const logoUrl = data.trim().replaceAll(' ', '%2520'); 
      content = 
      <button
        className="ma-0"
        type="button"
        onClick={ () => { toggleImageSize((imageSize === '2rem') ? '10rem' : '2rem'); }}
      >
        <ImageFromAzureWithUri
          id={`js-logo-${leadId}`}
          alt={data}
          fileUri={`http://lb-jcl.s3.us-west-2.amazonaws.com/${formatBucketFolder(campaignId)}/dev-${logoUrl}`}
          style={{
            height: imageSize,
            display: 'inline-block',
            marginTop: '0.5rem',
            marginBottom: '0.5rem',
            transition: 'height 0.15s ease-out',
          }}
        />
      </button>;
    } else {
      content = 'No image associated.';
    }
  }
  if (isLink) {
    if (data) {
      content = <a href={data}>View Material</a>;
    } else {
      content = <em>Material not available.</em>;
    }
  }

  return (
    <TableData theme="light">
      {content}
    </TableData>
  );
};

function setLogoDimensionsForOrganization (logo: HTMLImageElement, organizationId: number, options: {
  coBrandId?: number;
  reportId?: number;
}) {
  axios.post('/update_organization/', {
    organizationId,
    coBrandId: options.coBrandId,
    reportId: options.reportId,
    key: 'logo_height',
    value: logo.height,
  });
  axios.post('/update_organization/', {
    organizationId,
    coBrandId: options.coBrandId,
    reportId: options.reportId,
    key: 'logo_width',
    value: logo.width,
  });
}

function setBrandColorForOrganization (color: string, organizationId: number, opts: {
  coBrandId?: number;
  reportId?: number;
}) {
  axios.post('/update_organization/', {
    organizationId,
    coBrandId: opts.coBrandId,
    reportId: opts.reportId,
    key: 'brand_color',
    value: color,
  });
}

const colorThief = new ColorThief();
function getColorFromLogo (logo: HTMLImageElement, colorCount = 5, quality = 1) {
  const colors = colorThief.getPalette(logo, colorCount, quality);
  const rgbs = _.map(colors, (rgbArr) => {
    // NOTE: rgb strings must include spaces after comma to match `color`
    return `rgb(${rgbArr[0]}, ${rgbArr[1]}, ${rgbArr[2]})`;
  });
  return _.uniq(rgbs);
}

const LeadListRow = ({ campaignId, cols, data, brandColorIndex, logoIndex, linkIndex, includeIndices }): JSX.Element => {
  const [ showDetails, toggleShowDetails ] = useState(false);
  const Details: JSX.Element[] = [];
  const Cols = _.map(cols, (col, i) => {
    const params = {
      key: i,
      campaignId,
      leadId: data.id,
      organizationId: data.organizationId,
      reportId: data.reportId,
      data: data[col],
      isBrandColor: brandColorIndex === i,
      isLogo: logoIndex === i,
      isLink: linkIndex === i,
    };
    if (_.indexOf(includeIndices, i) > -1) {
      Details.push(
        <div key={Details.length}>
          <strong>{col}</strong> {data[col]}
        </div>
      );
    }
    else {
      return <LeadListItemCol {...params} />;
    }
  });
  return (
    <> 
      <TableRow theme="light">
        {Cols}
        <TableData theme="light">
          <button
            type="button"
            style={{
              borderColor: 'black',
            }}
            onClick={ () => toggleShowDetails(!showDetails)}
            className="my-2"
            >
            { showDetails ? 'Hide' : 'Show'} Details
          </button>
        </TableData>
      </TableRow>
      {
        showDetails &&
        <TableRow theme="light">
          <TableData 
            colSpan={100}
            theme="light"
          >
            {Details}
          </TableData>
        </TableRow>
      }
    </>
  );
};

// TODO: If logos update, re-load logos
const LeadsPage = (): JSX.Element => {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [leads, setLeads] = useState(null);
  const [leadEls, setLeadEls] = useState<JSX.Element[]>(null);
  const [headerEl, setHeaderEl] = useState<JSX.Element>(null);
  const includeCols = [
    'organizationName',
    'zipCode',
    'logoUrl',
    'brandColor',
    'radius',
    'link',
  ];
  const includeIndices: number[] = [];
  let logoIndex = -1;
  let brandColorIndex = -1;
  let linkIndex = -1;
  useEffect(() => {
    fetch_campaign(campaignId, setCampaign);
    fetch_leads(campaignId, setLeads);
  }, []);
  useEffect(() => {
    if (leads) {
      const _leadEls = [];
      const _headerEls = [];
      const cols = Object.keys(leads[0]);
      brandColorIndex = _.indexOf(cols, 'brandColor');
      logoIndex = _.indexOf(cols, 'logoUrl');
      linkIndex = _.indexOf(cols, 'link');

      for (let i = 0; i < cols.length; i ++) {
        if (_.indexOf(includeCols, cols[i]) > -1) {
          _headerEls.push(<TableHeader style={{ padding: '1rem' }} key={i}>{cols[i]}</TableHeader>);
        } else {
          includeIndices.push(i);
        }
      }
      _headerEls.push(<TableHeader style={{ padding: '1rem' }} key={cols.length}></TableHeader>);
      setHeaderEl(
        <TableRow>
          {_headerEls}
        </TableRow>
      );
      for (let i = 0; i < leads.length; i++) {
        if (leads[i].errors && leads[i].errors.length) {
          _leadEls.push(
            <TableRow theme="light">
              <TableData
                theme="light"
                colSpan={100}
              >
                {JSON.stringify(leads[i].errors)}
              </TableData>
            </TableRow>
          );
        } else {
          const params = {
            key: i,
            campaignId,
            cols,
            brandColorIndex,
            logoIndex,
            linkIndex,
            includeIndices,
            data: leads[i],
          };
          _leadEls.push( <LeadListRow {...params}  /> );
        }
      }
      setLeadEls(_leadEls);
    }
  }, [leads]);
  return (
    <StandardPageTemplate>
      { 
        leads && campaign 
          ? 
            <>
              <p className="f-small">
                Leads for Campaign { campaign?.name } { campaign.createdAt }
              </p>
              <UploadLogos campaignId={campaignId} />
              <hr />
              <button type="button" className="mr-2" onClick={() => { handleDownload(leads, campaign.name); }}>Download CSV</button>
              <button type="button" onClick={() => { handleGenerate(leads, campaign.id); }}>Generate Reports</button>
              <Table theme="light">
                <TableHead>
                  { headerEl }
                </TableHead>
                <TableBody>
                  { leadEls }
                </TableBody>
              </Table>
            </>
          : 'Loading...'
      }
    </StandardPageTemplate>
  );
};

export default LeadsPage;
